const BASE_URL = process.env.REACT_APP_BASE_URL || "";

// Multi-company view: the other company of the group a super admin picked in the header dropdown (null = their own).
// It is sent as distributorId on every request, so the super admin works in that company exactly as in their own.
const VIEW_COMPANY_KEY = "viewCompanyId";

const getViewCompanyId = () => sessionStorage.getItem(VIEW_COMPANY_KEY);

const setViewCompanyId = (distributorId) => {
  const own = sessionStorage.getItem("distributorId");
  if (distributorId === null || distributorId === undefined || distributorId === "" || String(distributorId) === own) {
    sessionStorage.removeItem(VIEW_COMPANY_KEY);
  } else {
    sessionStorage.setItem(VIEW_COMPANY_KEY, String(distributorId));
  }
};

const withViewCompany = (params) => {
  const companyId = getViewCompanyId();
  return companyId ? { ...(params || {}), distributorId: companyId } : params;
};

const buildHeaders = (hasBody) => {
  const token = sessionStorage.getItem("accessToken");
  const headers = { Accept: "application/json" };
  if (hasBody) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const parseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
};

const extractErrorMessage = (data, response) => {
  if (data?.errors && Array.isArray(data.errors) && data.errors.length) {
    return data.errors.map((e) => e.message || e.defaultMessage).filter(Boolean).join("; ");
  }
  return data?.detail || data?.message || data?.error || `Request failed with status ${response.status}`;
};

const request = async (path, { method = "GET", body, params } = {}) => {
  params = withViewCompany(params);
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, value);
      }
    });
  }

  const response = await fetch(url.toString(), {
    method,
    headers: buildHeaders(body !== undefined),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const data = await parseBody(response);

  if (response.status === 401) {
    sessionStorage.clear();
    window.location.href = "/";
    throw new Error("Session expired. Please log in again.");
  }

  if (!response.ok) {
    throw new Error(extractErrorMessage(data, response));
  }

  return data;
};

const buildUrl = (path, params) => {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, value);
      }
    });
  }
  return url.toString();
};

const uploadFile = async (path, file, params, extraHeaders) => {
  const token = sessionStorage.getItem("accessToken");
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(buildUrl(path, withViewCompany(params)), {
    method: "POST",
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(extraHeaders || {}),
    },
    body: formData,
  });

  const data = await parseBody(response);

  if (response.status === 401) {
    sessionStorage.clear();
    window.location.href = "/";
    throw new Error("Session expired. Please log in again.");
  }

  if (!response.ok) {
    throw new Error(extractErrorMessage(data, response));
  }

  return data;
};

const downloadFile = async (path, params, filename) => {
  const token = sessionStorage.getItem("accessToken");
  const response = await fetch(buildUrl(path, withViewCompany(params)), {
    method: "GET",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (response.status === 401) {
    sessionStorage.clear();
    window.location.href = "/";
    throw new Error("Session expired. Please log in again.");
  }

  if (!response.ok) {
    const data = await parseBody(response);
    throw new Error(extractErrorMessage(data, response));
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

// Opens an authenticated file (e.g. a PDF) in a new tab for viewing / printing. The tab is opened before the request so the
// browser does not treat it as an unsolicited popup.
const openFile = async (path, params) => {
  const tab = window.open("", "_blank");
  const token = sessionStorage.getItem("accessToken");
  try {
    const response = await fetch(buildUrl(path, withViewCompany(params)), {
      method: "GET",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (response.status === 401) {
      sessionStorage.clear();
      window.location.href = "/";
      throw new Error("Session expired. Please log in again.");
    }

    if (!response.ok) {
      const data = await parseBody(response);
      throw new Error(extractErrorMessage(data, response));
    }

    const url = URL.createObjectURL(await response.blob());
    if (tab) tab.location.href = url;
    else window.location.assign(url);
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (error) {
    if (tab) tab.close();
    throw error;
  }
};

// Fetches an authenticated file (e.g. an image) as a Blob, for showing it inside the page.
const fetchBlob = async (path, params) => {
  const token = sessionStorage.getItem("accessToken");
  const response = await fetch(buildUrl(path, withViewCompany(params)), {
    method: "GET",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (response.status === 401) {
    sessionStorage.clear();
    window.location.href = "/";
    throw new Error("Session expired. Please log in again.");
  }

  if (!response.ok) {
    const data = await parseBody(response);
    throw new Error(extractErrorMessage(data, response));
  }

  return response.blob();
};

// Every row of a paged list endpoint, fetched page by page (the server caps how many rows one page may hold).
// Resolves to the same { content } shape a single page has.
const ALL_PAGE_SIZE = 1000;
const getAll = async (path, params) => {
  const content = [];
  for (let page = 0; ; page++) {
    const data = await request(path, { method: "GET", params: { ...params, page, size: ALL_PAGE_SIZE, sort: "id" } });
    const rows = data?.content || [];
    content.push(...rows);
    if (rows.length < ALL_PAGE_SIZE) return { content };
  }
};

const api = {
  get: (path, params) => request(path, { method: "GET", params }),
  getAll,
  post: (path, body, params) => request(path, { method: "POST", body: body ?? {}, params }),
  put: (path, body, params) => request(path, { method: "PUT", body: body ?? {}, params }),
  del: (path) => request(path, { method: "DELETE" }),
  uploadFile,
  downloadFile,
  openFile,
  fetchBlob,
};

export default api;
// The distributor the panel is working in: the picked company, or the user's own.
const getActiveDistributorId = () => getViewCompanyId() || sessionStorage.getItem("distributorId") || "";

export { BASE_URL, getActiveDistributorId, getViewCompanyId, setViewCompanyId };
